import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPercentage, faDollar } from '@fortawesome/pro-regular-svg-icons';
import { faRight } from '@fortawesome/pro-solid-svg-icons';
import { NSInput, NSRadioOption } from 'bricks';
import NSModal from 'bricks/NSModal/NSModal';
import { numeralFormatterCurrency, roundToDecimals } from 'ns_libs/formatter';
import { useDebounce } from 'hooks/useDebounce';
import { Deal } from 'views/Deals/types';
import { useCalculateResidualLandValue } from './hooks/useCalculateResidualLandValue';
import { BOESlideoutFormValues } from '../SlideoutForm/types';
import {
    ReturnType,
    ITargetReturns,
    IResidualLandValueResult,
} from './types';
import './ResidualLandValueModal.scss';

export interface IResidualLandValueModalProps {
    isOpen: boolean;
    toggle: () => void;
    onConfirm: (result: IResidualLandValueResult) => void;
    boeValues: BOESlideoutFormValues;
    deal?: Deal;
}

const ResidualLandValueModal: React.FC<IResidualLandValueModalProps> = ({
    isOpen,
    toggle,
    onConfirm,
    boeValues,
    deal,
}) => {
    const [targetROC, setTargetROC] = useState<number | null>(
        deal?.targetReturnOnCost ? deal.targetReturnOnCost : null,
    );
    const [targetROE, setTargetROE] = useState<number | null>(
        deal?.targetReturnOnEquity ? deal.targetReturnOnEquity : null,
    );
    const [targetProfit, setTargetProfit] = useState<number | null>(
        deal?.targetProfitReturn ?? null,
    );

    const debouncedTargetROC = useDebounce(targetROC, 300);
    const debouncedTargetROE = useDebounce(targetROE, 300);
    const debouncedTargetProfit = useDebounce(targetProfit, 300);

    const [acquisitionFromROC, setAcquisitionFromROC] = useState<number | null>(null);
    const [acquisitionFromROE, setAcquisitionFromROE] = useState<number | null>(null);
    const [acquisitionFromProfit, setAcquisitionFromProfit] = useState<number | null>(null);

    const [selectedReturn, setSelectedReturn] = useState<ReturnType | null>(null);

    const { mutate: calculateResidual, isPending } = useCalculateResidualLandValue();

    const lowestAcquisitionCostType = useMemo((): ReturnType | null => {
        const acquisitionValues: { type: ReturnType, value: number }[] = [
            { type: 'ROC' as const, value: acquisitionFromROC ?? Number.MAX_VALUE },
            { type: 'ROE' as const, value: acquisitionFromROE ?? Number.MAX_VALUE },
            { type: 'PROFIT' as const, value: acquisitionFromProfit ?? Number.MAX_VALUE },
        ].filter(item => item.value > 0 && item.value !== Number.MAX_VALUE);

        if (acquisitionValues.length === 0) return null;

        const min = Math.min(...acquisitionValues.map(item => item.value));

        const epsilon = 1e-2;
        const lowest = acquisitionValues.find(item => Math.abs(item.value - min) < epsilon);

        return lowest ? lowest.type : null;
    }, [acquisitionFromROC, acquisitionFromROE, acquisitionFromProfit]);

    useEffect(() => {
        if (!isOpen) return;

        const hasAtLeastOneTarget = debouncedTargetROC !== null || debouncedTargetROE !== null || debouncedTargetProfit !== null;
        if (!hasAtLeastOneTarget) return;

        calculateResidual(
            {
                boeValues,
                targetReturnOnCost: debouncedTargetROC,
                targetReturnOnEquity: debouncedTargetROE,
                targetProfitReturn: debouncedTargetProfit,
            },
            {
                onSuccess: data => {
                    setAcquisitionFromROC(data.acquisitionFromRoc);
                    setAcquisitionFromROE(data.acquisitionFromRoe);
                    setAcquisitionFromProfit(data.acquisitionFromProfit);
                },
                onError: error => {
                    console.error('Failed to calculate residual land value:', error);

                    setAcquisitionFromROC(null);
                    setAcquisitionFromROE(null);
                    setAcquisitionFromProfit(null);
                },
            },
        );
    }, [isOpen, debouncedTargetROC, debouncedTargetROE, debouncedTargetProfit, boeValues, calculateResidual]);

    const scenarioKey = boeValues?.id ?? 'new';

    useEffect(() => {
        setTargetROC(deal?.targetReturnOnCost ? deal.targetReturnOnCost : null);
        setTargetROE(deal?.targetReturnOnEquity ? deal.targetReturnOnEquity : null);
        setTargetProfit(deal?.targetProfitReturn ?? null);

        setAcquisitionFromROC(null);
        setAcquisitionFromROE(null);
        setAcquisitionFromProfit(null);
        setSelectedReturn(null);
    }, [scenarioKey, deal?.targetReturnOnCost, deal?.targetReturnOnEquity, deal?.targetProfitReturn]);

    const handleCancel = () => {
        toggle();
    };

    const handleUseSelected = () => {
        if (!selectedReturn) return;

        const acquisitionMap: Record<ReturnType, number | null> = {
            ROC: acquisitionFromROC,
            ROE: acquisitionFromROE,
            PROFIT: acquisitionFromProfit,
        };

        const acquisitionCost = acquisitionMap[selectedReturn];
        if (acquisitionCost === null) return;

        const roundedAcquisitionCost = roundToDecimals(acquisitionCost, 2);

        const targetReturns: ITargetReturns = {
            returnOnCost: targetROC,
            returnOnEquity: targetROE,
            profitReturn: targetProfit,
        };

        onConfirm({
            acquisitionCost: roundedAcquisitionCost,
            selectedReturn,
            targetReturns,
        });
    };

    const renderAcquisitionValue = (value: number | null, errorMessage: string): React.ReactNode => {
        if (value === null) return '-';

        if (value <= 0) {
            return errorMessage;
        }

        return numeralFormatterCurrency(value);
    };

    const isExceedError = (value: number | null): boolean => value !== null && value <= 0;

    const canUseSelected = selectedReturn !== null && (() => {
        const map: Record<ReturnType, number | null> = {
            ROC: acquisitionFromROC,
            ROE: acquisitionFromROE,
            PROFIT: acquisitionFromProfit,
        };

        const selectedReturnValue = map[selectedReturn];
        return selectedReturnValue && selectedReturnValue > 0;
    })();

    return (
        <NSModal
            isOpen={isOpen}
            toggle={toggle}
            modalClassName="ResidualLandValueModal heap-deal-BoE-calculate-land-value-confirm-returns"
            modalHeaderText="Residual land value calculator"
            modalHeaderClassName="bg-primary text-white"
            modalFooterButtonText="Use selected"
            modalFooterCancelButtonText="Cancel"
            modalFooterButtonColor="primary"
            modalFooterCancelButtonFunction={handleCancel}
            modalFooterButtonFunction={handleUseSelected}
            modalFooterIsButtonFunctionDisabled={!canUseSelected || isPending}
        >
            <div className="text-white mb-3">
                Calculate your acquisition budget using one of the three target return metrics below:
            </div>

            <div className="ResidualLandValueModal__content d-flex align-items-start">
                {/* Target returns table */}
                <div className="ResidualLandValueModal__target-returns-table d-flex">
                    {/* Radio column */}
                    <div className="d-flex flex-column">
                        <div className="ResidualLandValueModal__header-cell d-flex align-items-center font-weight-bold font-12 text-white" />
                        <div className="ResidualLandValueModal__data-cell ResidualLandValueModal__data-cell--radio d-flex align-items-center text-white">
                            <NSRadioOption
                                id="rocOption"
                                name="returnOption"
                                label=""
                                checked={selectedReturn === 'ROC'}
                                onChange={() => setSelectedReturn('ROC')}
                                disabled={acquisitionFromROC === null || acquisitionFromROC <= 0}
                            />
                        </div>
                        <div className="ResidualLandValueModal__data-cell ResidualLandValueModal__data-cell--radio d-flex align-items-center text-white">
                            <NSRadioOption
                                id="roeOption"
                                name="returnOption"
                                label=""
                                checked={selectedReturn === 'ROE'}
                                onChange={() => setSelectedReturn('ROE')}
                                disabled={acquisitionFromROE === null || acquisitionFromROE <= 0}
                            />
                        </div>
                        <div className="ResidualLandValueModal__data-cell ResidualLandValueModal__data-cell--radio d-flex align-items-center text-white">
                            <NSRadioOption
                                id="profitOption"
                                name="returnOption"
                                label=""
                                checked={selectedReturn === 'PROFIT'}
                                onChange={() => setSelectedReturn('PROFIT')}
                                disabled={acquisitionFromProfit === null || acquisitionFromProfit <= 0}
                            />
                        </div>
                    </div>

                    {/* Label column */}
                    <div className="d-flex flex-column">
                        <div className="ResidualLandValueModal__header-cell d-flex align-items-center font-weight-bold font-12 text-white" />
                        <div className="ResidualLandValueModal__data-cell ResidualLandValueModal__data-cell--label d-flex align-items-center text-white">
                            Return on cost
                        </div>
                        <div className="ResidualLandValueModal__data-cell ResidualLandValueModal__data-cell--label d-flex align-items-center text-white">
                            Return on equity
                        </div>
                        <div className="ResidualLandValueModal__data-cell ResidualLandValueModal__data-cell--label d-flex align-items-center text-white">
                            Profit return
                        </div>
                    </div>

                    {/* Target column */}
                    <div className="d-flex flex-column">
                        <div className="ResidualLandValueModal__header-cell d-flex align-items-center font-weight-bold font-12 text-white">Target</div>
                        <div className="ResidualLandValueModal__data-cell ResidualLandValueModal__data-cell--target d-flex align-items-center text-white">
                            <NSInput
                                id="targetROC"
                                type="number"
                                name="targetROC"
                                placeholder="Enter"
                                value={targetROC ?? ''}
                                appendInputAddon={<FontAwesomeIcon icon={faPercentage} />}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTargetROC(e.target.valueAsNumber)}
                            />
                        </div>
                        <div className="ResidualLandValueModal__data-cell ResidualLandValueModal__data-cell--target d-flex align-items-center text-white">
                            <NSInput
                                id="targetROE"
                                type="number"
                                name="targetROE"
                                placeholder="Enter"
                                value={targetROE ?? ''}
                                appendInputAddon={<FontAwesomeIcon icon={faPercentage} />}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTargetROE(e.target.valueAsNumber)}
                            />
                        </div>
                        <div className="ResidualLandValueModal__data-cell ResidualLandValueModal__data-cell--target d-flex align-items-center text-white">
                            <NSInput
                                id="targetProfit"
                                type="number"
                                name="targetProfit"
                                placeholder="Enter"
                                value={targetProfit ?? ''}
                                prependInputAddon={<FontAwesomeIcon icon={faDollar} />}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    const val = e.target.valueAsNumber;
                                    if (Number.isNaN(val)) {
                                        setTargetProfit(null);
                                    } else {
                                        setTargetProfit(Math.max(val, 0));
                                    }
                                }}
                            />
                        </div>
                    </div>
                </div>

                {/* Arrow column */}
                <div className="ResidualLandValueModal__arrow-column d-flex flex-column justify-content-start">
                    <div className="ResidualLandValueModal__arrow-cell d-flex align-items-center justify-content-center">
                        <FontAwesomeIcon icon={faRight} />
                    </div>
                    <div className="ResidualLandValueModal__arrow-cell d-flex align-items-center justify-content-center">
                        <FontAwesomeIcon icon={faRight} />
                    </div>
                    <div className="ResidualLandValueModal__arrow-cell d-flex align-items-center justify-content-center">
                        <FontAwesomeIcon icon={faRight} />
                    </div>
                </div>

                {/* Acquisition cost table */}
                <div className="ResidualLandValueModal__acquisition-cost-table">
                    <div className="d-flex flex-column">
                        <div className="ResidualLandValueModal__header-cell d-flex align-items-center font-weight-bold font-12 text-white">
                            Acquisition cost
                        </div>
                        <div
                            className={classNames(
                                'ResidualLandValueModal__data-cell d-flex align-items-center text-white',
                                'ResidualLandValueModal__data-cell--result',
                                {
                                    'ResidualLandValueModal__data-cell--lowest-acquisition-cost': lowestAcquisitionCostType === 'ROC',
                                    'ResidualLandValueModal__data-cell--exceed-error': isExceedError(acquisitionFromROC),
                                },
                            )}
                        >
                            {renderAcquisitionValue(acquisitionFromROC, 'Returns + costs exceed exit value')}
                        </div>
                        <div
                            className={classNames(
                                'ResidualLandValueModal__data-cell d-flex align-items-center text-white',
                                'ResidualLandValueModal__data-cell--result',
                                {
                                    'ResidualLandValueModal__data-cell--lowest-acquisition-cost': lowestAcquisitionCostType === 'ROE',
                                    'ResidualLandValueModal__data-cell--exceed-error': isExceedError(acquisitionFromROE),
                                },
                            )}
                        >
                            {renderAcquisitionValue(acquisitionFromROE, 'Returns + costs exceed exit value')}
                        </div>
                        <div
                            className={classNames(
                                'ResidualLandValueModal__data-cell d-flex align-items-center text-white',
                                'ResidualLandValueModal__data-cell--result',
                                {
                                    'ResidualLandValueModal__data-cell--lowest-acquisition-cost': lowestAcquisitionCostType === 'PROFIT',
                                    'ResidualLandValueModal__data-cell--exceed-error': isExceedError(acquisitionFromProfit),
                                },
                            )}
                        >
                            {renderAcquisitionValue(acquisitionFromProfit, 'Profit + costs exceed exit value')}
                        </div>
                    </div>
                </div>
            </div>
        </NSModal>
    );
};

export default ResidualLandValueModal;
